import { adminApi, ensureArray } from '../services/api';
import { useState, useEffect } from 'react';

interface CorporateAccount {
  id: string;
  companyName: string;
  contactEmail: string;
  employeeEmailDomain: string;
  sessionAllowance: number;
  approved: boolean;
  createdAt: string;
  usedSessions: number;
}

export function CorporateApprovalsPage() {
  const [accounts, setAccounts] = useState<CorporateAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<'pending' | 'approved'>('pending');
  const [acting, setActing] = useState<string | null>(null);

  const load = () => {
    setLoading(true);
    adminApi<CorporateAccount[]>(`/admin/corporate?approved=${tab === 'approved'}`)
      .then((d) => setAccounts(ensureArray(d)))
      .catch(() => setAccounts([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, [tab]);

  const approve = async (id: string) => {
    setActing(id);
    try {
      await adminApi(`/admin/corporate/${id}/approve`, { method: 'PATCH' });
      load();
    } finally {
      setActing(null);
    }
  };

  const reject = async (id: string) => {
    setActing(id);
    try {
      await adminApi(`/admin/corporate/${id}/reject`, { method: 'PATCH' });
      load();
    } finally {
      setActing(null);
    }
  };

  const updateAllowance = async (id: string, allowance: number) => {
    await adminApi(`/admin/corporate/${id}/allowance`, {
      method: 'PATCH',
      body: JSON.stringify({ sessionAllowance: allowance }),
    });
    load();
  };

  return (
    <div className="p-6">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Corporate Accounts</h1>
          <p className="mt-0.5 text-sm text-gray-500">
            Review and manage B2B company registrations
          </p>
        </div>
      </div>

      <div className="mb-6 flex gap-2">
        {(['pending', 'approved'] as const).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`rounded-full border px-4 py-1.5 text-sm font-medium transition-colors ${tab === t ? 'border-indigo-600 bg-indigo-600 text-white' : 'border-gray-200 bg-white text-gray-600 hover:border-indigo-300'}`}
          >
            {t.charAt(0).toUpperCase() + t.slice(1)}
          </button>
        ))}
      </div>

      <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
        {loading ? (
          <div className="p-8 text-center text-gray-400">Loading…</div>
        ) : accounts.length === 0 ? (
          <div className="p-12 text-center text-gray-400">No {tab} accounts.</div>
        ) : (
          <div className="divide-y divide-gray-100">
            {accounts.map((a) => (
              <div key={a.id} className="flex items-start justify-between gap-4 p-5">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <p className="font-semibold text-gray-900">{a.companyName}</p>
                    <span
                      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${a.approved ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}
                    >
                      {a.approved ? 'Approved' : 'Pending'}
                    </span>
                  </div>
                  <p className="mt-0.5 text-sm text-gray-500">
                    {a.contactEmail} · @{a.employeeEmailDomain}
                  </p>
                  <p className="mt-1 text-xs text-gray-400">
                    Applied{' '}
                    {new Date(a.createdAt).toLocaleDateString('en-NG', {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric',
                    })}
                  </p>
                  {a.approved && (
                    <p className="mt-1 text-xs text-gray-500">
                      Sessions: {a.usedSessions} / {a.sessionAllowance} used
                    </p>
                  )}
                </div>
                <div className="flex flex-shrink-0 items-center gap-2">
                  {!a.approved && (
                    <>
                      <button
                        onClick={() => approve(a.id)}
                        disabled={acting === a.id}
                        className="rounded-lg bg-green-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-50"
                      >
                        {acting === a.id ? '…' : 'Approve'}
                      </button>
                      <button
                        onClick={() => reject(a.id)}
                        disabled={acting === a.id}
                        className="rounded-lg border border-red-300 px-3 py-1.5 text-sm font-medium text-red-600 hover:bg-red-50 disabled:opacity-50"
                      >
                        Reject
                      </button>
                    </>
                  )}
                  {a.approved && (
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        defaultValue={a.sessionAllowance}
                        min={0}
                        className="w-20 rounded-lg border border-gray-300 px-2 py-1 text-center text-sm focus:border-indigo-500 focus:outline-none"
                        onBlur={(e) => updateAllowance(a.id, parseInt(e.target.value))}
                        aria-label="Session allowance"
                      />
                      <span className="text-xs text-gray-400">sessions/mo</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
